import type { ConnectionPositionPair } from '@angular/cdk/overlay';
import type { SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  output,
  input,
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';

import { I18nService } from '@core/i18n-legacy';

import type { InputSelectDropdownOption } from './input-with-select-dropdown/input-with-select-dropdown.component';
import { InputWithSelectDropdownComponent } from './input-with-select-dropdown/input-with-select-dropdown.component';
import {
  ConnectedOverlayDirective,
  OverlayOriginDirective,
} from '../../core/overlay/overlay-directives';
import { InputComponent } from '../input/input.component';

@Component({
  selector: 'rt-input-with-select',
  templateUrl: './input-with-select.component.html',
  styleUrls: ['./input-with-select.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ConnectedOverlayDirective,
    InputComponent,
    InputWithSelectDropdownComponent,
    OverlayOriginDirective,
    ReactiveFormsModule,
  ],
})
export class InputWithSelectComponent {
  public readonly dropdownMaxHeight = input<number>(300);
  public readonly dropdownIconHasMargin = input<boolean>(true);
  public readonly value = input<string | number | null>(null);
  public readonly options = input<InputSelectDropdownOption[]>([]);
  public readonly translationKey = input<string>(undefined!);
  public readonly noLeftPadding = input<boolean>(false);
  public readonly placeholder = input<string>('');
  public readonly invalid = input<boolean>(undefined!);

  public valueChanges = output<string | null | number>();

  public dropdownPositions: ConnectionPositionPair[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
    { originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'top' },
  ];
  public dropdownWidth: number = 132;
  public valueControl: UntypedFormControl;
  public currentOption: InputSelectDropdownOption | null;
  public dropdownOpen = false;

  constructor(
    protected cdRef: ChangeDetectorRef,
    private i18nService: I18nService,
  ) {}

  public ngOnInit(): void {
    const value = this.value();
    if (value !== null) {
      this.currentOption = this.getOptionFromValue(value);
    }

    this.valueControl = new UntypedFormControl(this.currentOption?.value, [
      Validators.pattern(/^[0-9]*$/),
    ]);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const value = this.value();
    if (changes.value && value !== null && this.valueControl) {
      this.currentOption = this.getOptionFromValue(value);
      this.valueControl.setValue(this.currentOption?.value, { emitEvent: false });
    }
  }

  public onInputChange(value: string): void {
    this.currentOption = this.getOptionFromValue(value);
    this.valueChanges.emit(this.currentOption?.value!);
  }

  public onValueSelect(option: InputSelectDropdownOption): void {
    this.valueControl.setValue(option.value);
    this.currentOption = option;
    this.valueChanges.emit(option.value);
  }

  public toggleDropdown(state: boolean | undefined = undefined): void {
    this.dropdownOpen = state === undefined ? !this.dropdownOpen : state;
    this.cdRef.detectChanges();
  }

  private getOptionFromValue(value: string | number | null): InputSelectDropdownOption | null {
    if (value === null) return null;

    const parsedValue = parseInt(<string>value, 10);
    if (Number.isNaN(parsedValue)) return { label: `${value}`, value };

    return {
      value: parsedValue,
      label: this.i18nService.translate(this.translationKey(), { count: parsedValue }),
    };
  }
}
